import { ChartOptions } from 'chart.js'
import upperFirst from 'lodash/upperFirst'
import React from 'react'
import { Chart } from 'react-chartjs-2'

import { useDatasetStyleProps, IChartJsDatasetStyles } from 'src/commons/chartjs'
import { getTranslation } from 'src/commons/i18n'

import { GranularityEnum, IGenericBucket } from '../types'
import { getDocCount, getDocCountWithoutPercentage, getUniqueCategories } from '../utils'

import { getChartPlugins, getLabelsWithGranularity, IGraphProps } from './graphCommons'
import { useGraphContext } from './GraphWrapper/GraphWrappers'
import { buildOptions } from './LinePlot'
import { DEFAULT_COLORS } from './themes'

export interface IComparisonBucket {
  average?: number
  compareBy: {
    buckets: { key: string }[]
  }
  key: string
}

const buildAverageDataset = (buckets: IComparisonBucket[]) => ({
  type: 'line',
  fill: false,
  data: buckets.map((b: IComparisonBucket): number => b.average || 0),
  borderColor: '#000000',
  backgroundColor: '#fff',
  borderWidth: 2,
  tension: 0.4,
})

function getDatasets(
  buckets: IComparisonBucket[],
  datasetStylesMapping: Record<string, IChartJsDatasetStyles>,
  formatLabel: (x: any) => string = (x: any) => x,
  showAverage?: boolean
) {
  const { t } = getTranslation()
  const compareBuckets = buckets
    .map((b: IComparisonBucket): { key: string }[] => b && b.compareBy && b.compareBy.buckets)
    .filter(Boolean)

  const uniqueCategories = getUniqueCategories(compareBuckets)
  let datasets: any = uniqueCategories.map((category: string) => {
    const datasetStyles = datasetStylesMapping[category]
    return {
      ...datasetStyles,
      label: formatLabel(category),
      fill: false,
      data: compareBuckets.map((bucket: IGenericBucket[]): number =>
        getDocCount(
          bucket.find((b: IGenericBucket) => b.key === category) ?? {
            doc_count: 0,
          }
        )
      ),
      tension: 0.4,
      additionalLegend: compareBuckets.map((bucket: IGenericBucket[]): number =>
        getDocCountWithoutPercentage(
          bucket.find((b: IGenericBucket) => b.key === category) ?? {
            doc_count_without_percentage: 0,
          }
        )
      ),
    }
  })

  if (showAverage) {
    // we show the average as a black line
    datasets = [
      ...datasets,
      {
        ...buildAverageDataset(buckets),
        label: upperFirst(t('average')),
      },
    ]
  }

  return datasets
}

export function getDatasetsWithPreviousYear(
  bucketsReferenceYear: IComparisonBucket[],
  bucketsPreviousYear: IComparisonBucket[],
  datasetStylesMapping: Record<string, IChartJsDatasetStyles>,
  formatLabel: (x: any) => string = (x: any) => x,
  showAverage?: boolean
) {
  const { t } = getTranslation()
  const previousYearSuffix = t('previous year')

  const compareBucketsReferenceYear = bucketsReferenceYear
    .map((b: IComparisonBucket): { key: string }[] => b && b.compareBy && b.compareBy.buckets)
    .filter(Boolean)
  const compareBucketsPreviousYear = bucketsPreviousYear
    .map((b: IComparisonBucket): { key: string }[] => b && b.compareBy && b.compareBy.buckets)
    .filter(Boolean)

  const uniqueCategoriesReferenceYear = getUniqueCategories(compareBucketsReferenceYear)
  const uniqueCategoriesPreviousYear = getUniqueCategories(compareBucketsPreviousYear)
  const uniqueCategories = [...new Set([...uniqueCategoriesReferenceYear, ...uniqueCategoriesPreviousYear])].sort()

  let datasets: any = uniqueCategories
    .map((category: string) => {
      const datasetStyles = datasetStylesMapping[category]
      return [
        {
          ...datasetStyles,
          // previous year data is shown with dotted lines
          borderDash: [10, 5],
          label: `${formatLabel(category)} (${previousYearSuffix})`,
          fill: false,
          data: compareBucketsPreviousYear.map((bucket: IGenericBucket[]): number =>
            getDocCount(
              bucket.find((b: IGenericBucket) => b.key === category) || {
                doc_count: 0,
              }
            )
          ),
          tension: 0.4,
          additionalLegend: compareBucketsPreviousYear.map((bucket: IGenericBucket[]): number =>
            getDocCountWithoutPercentage(
              bucket.find((b: IGenericBucket) => b.key === category) ?? {
                doc_count_without_percentage: 0,
              }
            )
          ),
        },
        {
          ...datasetStyles,
          label: formatLabel(category),
          fill: false,
          data: compareBucketsReferenceYear.map((bucket: IGenericBucket[]): number =>
            getDocCount(
              bucket.find((b: IGenericBucket) => b.key === category) || {
                doc_count: 0,
              }
            )
          ),
          tension: 0.4,
          additionalLegend: compareBucketsReferenceYear.map((bucket: IGenericBucket[]): number =>
            getDocCountWithoutPercentage(
              bucket.find((b: IGenericBucket) => b.key === category) ?? {
                doc_count_without_percentage: 0,
              }
            )
          ),
        },
      ]
    })
    .flat()

  if (showAverage) {
    // we show the average as a black line
    datasets = [
      ...datasets,
      {
        ...buildAverageDataset(bucketsPreviousYear),
        label: `${upperFirst(t('average'))} (${previousYearSuffix})`,
        // previous year data is shown with dotted lines
        borderDash: [10, 5],
      },
      {
        ...buildAverageDataset(bucketsReferenceYear),
        label: upperFirst(t('average')),
      },
    ]
  }

  return datasets
}

interface IProps extends IGraphProps {
  enableAnimation?: boolean
  formatLabel?: (x: any) => string
  granularity?: GranularityEnum
  rawData: IComparisonBucket[]
  rawDataPreviousYear?: IComparisonBucket[]
  showAverage?: boolean
}

function ComparisonPlot({
  title = '',
  showTitle,
  w = 600,
  h = 300,
  rawData,
  rawDataPreviousYear = [],
  formatWithUnit,
  maxValue,
  enableAnimation = true,
  formatLabel,
  granularity,
  yTicksFormatter,
  xTicksFormatter,
  datasetCategory,
  showAverage = true,
}: IProps) {
  const { reactChart } = useGraphContext()

  const labels = granularity
    ? getLabelsWithGranularity(rawData, granularity)
    : rawData.map((bucket: IGenericBucket) => bucket.key as string)
  const datasetStylesMapping = useDatasetStyleProps(datasetCategory, DEFAULT_COLORS)
  const datasets =
    rawDataPreviousYear && rawDataPreviousYear.length
      ? getDatasetsWithPreviousYear(rawData, rawDataPreviousYear, datasetStylesMapping, formatLabel, showAverage)
      : getDatasets(rawData, datasetStylesMapping, formatLabel, showAverage)

  const plugins = getChartPlugins()

  return (
    <Chart
      title={title}
      type="line"
      ref={reactChart.chartRef}
      data={{ labels, datasets }}
      options={
        buildOptions({
          title,
          showTitle,
          formatWithUnit,
          maxValue,
          yTicksFormatter,
          xTicksFormatter,
          enableAnimation,
        }) as ChartOptions<'line'>
      }
      width={w}
      height={h}
      plugins={plugins}
    />
  )
}

export default ComparisonPlot
